import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { config } from '@/lib/config';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { cn } from '@/lib/utils';
import { Bot, Boxes, CheckCircle2, PlaneTakeoff, Radio, Route, Sparkles } from 'lucide-react';
import { type ReactNode, useEffect, useMemo } from 'react';
import {
  AI_AGENT_CONTEXTS,
  APPS_CONTEXT_LABEL,
  type AppActivity,
  type AppId,
  MISSION_TEMPLATES,
  useAppsStore,
} from './AppsStore';

const INPUT =
  'w-full rounded-md border border-(--color-border) bg-(--color-bg) px-2.5 py-2 text-[0.846em] outline-none transition-colors focus:border-(--color-green)';

const AI_ACTIVITY_TEMPLATES: Record<(typeof AI_AGENT_CONTEXTS)[number], string> = {
  drones: 'Flagged a drone status anomaly',
  oi: 'Summarized active Operational Intent conflicts',
  surv: 'Explained a DAA-degraded surveillance alert',
  infra: 'Reported a sensor offline event',
  rtm: 'Investigated a degraded RTM service',
  inv: 'Answered a fleet inventory question',
};

export function AppsOverlay() {
  const selectedApp = useAppsStore((s) => s.selectedApp);
  const activeTab = useAppsStore((s) => s.activeTab);
  const selectApp = useAppsStore((s) => s.selectApp);
  const setActiveTab = useAppsStore((s) => s.setActiveTab);
  const appendActivity = useAppsStore((s) => s.appendActivity);
  const aiEnabled = useAppsStore((s) => s.aiEnabled);
  const djiConfigured = useAppsStore((s) => s.dji.configured);
  const djiTopic = useAppsStore((s) => s.dji.brokerTopic);
  const topicCount = useDataStreamStore((s) => s.topics.get(djiTopic)?.count ?? 0);

  useEffect(() => {
    if (!config.isMock) return;
    const id = window.setInterval(() => {
      const enabled = AI_AGENT_CONTEXTS.filter((ctx) => aiEnabled[ctx]);
      if (enabled.length > 0) {
        const ctx = enabled[Math.floor(Math.random() * enabled.length)]!;
        appendActivity(
          'aiagent',
          `${AI_ACTIVITY_TEMPLATES[ctx]} (${APPS_CONTEXT_LABEL[ctx]})`,
          'runtime',
        );
      }
      if (djiConfigured && djiTopic) {
        appendActivity(
          'djifh2',
          `MQTT event matched on ${djiTopic} — triggered ${djiTriggerSummary()}`,
          'runtime',
        );
      }
    }, 9000);
    return () => window.clearInterval(id);
  }, [aiEnabled, appendActivity, djiConfigured, djiTopic]);

  const enabledCount =
    AI_AGENT_CONTEXTS.filter((ctx) => aiEnabled[ctx]).length + (djiConfigured ? 1 : 0);

  return (
    <div className="absolute inset-0 overflow-hidden bg-(--color-bg) p-3">
      <div className="grid h-full grid-cols-1 gap-3 lg:grid-cols-[270px_minmax(0,1fr)]">
        <section className="overflow-hidden rounded-md border border-(--color-border) bg-(--color-surface)">
          <header className="flex items-center justify-between border-b border-(--color-border-lite) bg-(--color-bg) px-3 py-2">
            <span className="text-[0.846em] font-bold">Apps</span>
            <span className="font-mono text-[0.615em] text-(--color-text3)">
              {enabledCount} enabled
            </span>
          </header>
          <div className="space-y-2 p-3">
            <AppListCard
              active={selectedApp === 'aiagent'}
              title="AI Agent"
              status={
                enabledCountForAi(aiEnabled) === 0
                  ? 'Not enabled'
                  : `${enabledCountForAi(aiEnabled)} of ${AI_AGENT_CONTEXTS.length} contexts enabled`
              }
              tone={enabledCountForAi(aiEnabled) > 0 ? 'ok' : 'muted'}
              onClick={() => selectApp('aiagent')}
              icon="🤖"
            />
            <AppListCard
              active={selectedApp === 'djifh2'}
              title="DJI FH2 Workflow Trigger"
              status={djiConfigured ? 'Connected' : 'Not configured'}
              tone={djiConfigured ? 'ok' : 'muted'}
              onClick={() => selectApp('djifh2')}
              icon="🛩️"
            />
          </div>
        </section>

        <section className="overflow-hidden rounded-md border border-(--color-border) bg-(--color-surface)">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-(--color-border-lite) bg-(--color-bg) px-3 py-2">
            <span className="text-[0.846em] font-bold">
              {selectedApp === 'aiagent'
                ? 'AI Agent'
                : selectedApp === 'djifh2'
                  ? 'DJI FH2 Workflow Trigger'
                  : 'Select an app'}
            </span>
            {selectedApp ? (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant={activeTab === 'config' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('config')}
                >
                  Configure
                </Button>
                <Button
                  size="sm"
                  variant={activeTab === 'monitor' ? 'default' : 'outline'}
                  onClick={() => setActiveTab('monitor')}
                >
                  Monitor
                </Button>
              </div>
            ) : null}
          </header>
          <div className="h-[calc(100%-49px)] overflow-y-auto p-3">
            {!selectedApp ? (
              <EmptyState />
            ) : activeTab === 'monitor' ? (
              <AppMonitor appId={selectedApp} topicCount={topicCount} />
            ) : selectedApp === 'aiagent' ? (
              <AIAgentConfig />
            ) : (
              <DjiConfigPanel />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-3 text-center text-(--color-text3)">
      <Boxes size={30} />
      <div className="text-[0.846em] font-medium">Select an app to configure</div>
    </div>
  );
}

function AppListCard({
  active,
  title,
  status,
  tone,
  onClick,
  icon,
}: {
  active: boolean;
  title: string;
  status: string;
  tone: 'ok' | 'muted';
  onClick: () => void;
  icon: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md border border-t-2 border-t-(--color-green) px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-(--color-green) bg-(--color-green-bg) outline outline-2 -outline-offset-2 outline-(--color-green)'
          : 'border-(--color-border) bg-(--color-surface) hover:border-(--color-green) hover:bg-(--color-green-bg)',
      )}
    >
      <span className="shrink-0 text-[1.5em] leading-none">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[0.923em] font-bold text-(--color-text)">{title}</span>
        <span
          className={cn(
            'mt-0.5 block text-[0.769em]',
            tone === 'ok' ? 'font-semibold text-(--color-green)' : 'text-(--color-text3)',
          )}
        >
          {status}
        </span>
      </span>
    </button>
  );
}

function AIAgentConfig() {
  const aiEnabled = useAppsStore((s) => s.aiEnabled);
  const toggleAiContext = useAppsStore((s) => s.toggleAiContext);
  const setAiAll = useAppsStore((s) => s.setAiAll);
  const appendActivity = useAppsStore((s) => s.appendActivity);
  const allOn = AI_AGENT_CONTEXTS.every((ctx) => aiEnabled[ctx]);

  const toggleOne = (ctx: (typeof AI_AGENT_CONTEXTS)[number]) => {
    const next = !aiEnabled[ctx];
    toggleAiContext(ctx);
    appendActivity(
      'aiagent',
      `AI Agent ${next ? 'enabled' : 'disabled'} for ${APPS_CONTEXT_LABEL[ctx]}`,
    );
  };

  const toggleAll = () => {
    const turnOn = !allOn;
    setAiAll(turnOn);
    appendActivity('aiagent', `AI Agent ${turnOn ? 'enabled' : 'disabled'} for all services`);
  };

  return (
    <div className="space-y-4">
      <p className="text-[0.846em] text-(--color-text3)">
        Enable the FLYsafe AI Agent to provide contextual assistance and automated insights within
        each context.
      </p>

      <div>
        <ToggleRow label="All Services" checked={allOn} onToggle={toggleAll} strong border />
        {AI_AGENT_CONTEXTS.map((ctx) => (
          <ToggleRow
            key={ctx}
            label={APPS_CONTEXT_LABEL[ctx]}
            checked={aiEnabled[ctx]}
            onToggle={() => toggleOne(ctx)}
          />
        ))}
        <div className="flex items-center justify-between py-[9px] opacity-[0.55]">
          <span className="text-[0.923em] text-(--color-text)">Data Stream</span>
          <span className="text-[0.769em] text-(--color-text3)">Not available</span>
        </div>
      </div>
    </div>
  );
}

function DjiConfigPanel() {
  const dji = useAppsStore((s) => s.dji);
  const setDjiField = useAppsStore((s) => s.setDjiField);
  const saveDjiConfig = useAppsStore((s) => s.saveDjiConfig);
  const appendActivity = useAppsStore((s) => s.appendActivity);
  const topicMap = useDataStreamStore((s) => s.topics);
  const topics = useMemo(() => Array.from(topicMap.keys()).sort(), [topicMap]);

  const onSave = () => {
    const result = saveDjiConfig();
    if (result.configured && !result.wasConfigured) {
      appendActivity(
        'djifh2',
        `Configuration saved — now watching ${dji.brokerTopic || 'the selected topic'} to trigger ${djiTriggerSummary()}`,
      );
    } else if (!result.configured && result.wasConfigured) {
      appendActivity('djifh2', 'Configuration incomplete — stopped watching MQTT broker');
    } else if (result.changed) {
      appendActivity('djifh2', 'Configuration updated');
    }
  };

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <SectionTitle>DJI FlightHub 2 Credentials</SectionTitle>
        <label className="block">
          <FieldLabel>Organization ID</FieldLabel>
          <input
            value={dji.orgId}
            onChange={(e) => setDjiField('orgId', e.target.value)}
            className={INPUT}
            placeholder="e.g. org-8f21ac"
          />
        </label>
        <label className="block">
          <FieldLabel>API Key</FieldLabel>
          <input
            value={dji.apiKey}
            onChange={(e) => setDjiField('apiKey', e.target.value)}
            type="password"
            className={INPUT}
            placeholder="FlightHub 2 API key"
          />
        </label>
      </section>

      <section className="space-y-2">
        <SectionTitle>MQTT Trigger Broker</SectionTitle>
        <p className="text-[0.769em] text-(--color-text3)">
          Uses the tenant&apos;s existing MQTT broker connection — no additional credentials
          required.
        </p>
        <label className="block">
          <FieldLabel>Broker Topic</FieldLabel>
          <select
            value={dji.brokerTopic}
            onChange={(e) => setDjiField('brokerTopic', e.target.value)}
            className={INPUT}
          >
            <option value="">Select a tenant broker topic…</option>
            {topics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="space-y-3">
        <SectionTitle>Trigger Event</SectionTitle>
        <div className="grid gap-2 md:grid-cols-2">
          <Button
            variant={dji.triggerType === 'workflow' ? 'default' : 'outline'}
            onClick={() => setDjiField('triggerType', 'workflow')}
          >
            DJI FH2 Automated Workflow
          </Button>
          <Button
            variant={dji.triggerType === 'mission' ? 'default' : 'outline'}
            onClick={() => setDjiField('triggerType', 'mission')}
          >
            Custom Flight Mission
          </Button>
        </div>

        {dji.triggerType === 'workflow' ? (
          <label className="block">
            <FieldLabel>Workflow Name / ID</FieldLabel>
            <input
              value={dji.workflowName}
              onChange={(e) => setDjiField('workflowName', e.target.value)}
              className={INPUT}
              placeholder="e.g. Perimeter-Patrol-01"
            />
          </label>
        ) : (
          <div className="space-y-2">
            <SectionTitle>Flight Mission Template</SectionTitle>
            <p className="text-[0.769em] text-(--color-text3)">
              Defined in the tenant configuration file. Only one mission is armed at a time.
            </p>
            <div className="space-y-2">
              {MISSION_TEMPLATES.map((template) => {
                const checked = dji.missionTemplateId === template.id;
                return (
                  <label
                    key={template.id}
                    className={cn(
                      'block rounded-md border p-3 transition-colors',
                      checked
                        ? 'border-(--color-green) bg-(--color-green-bg)'
                        : 'border-(--color-border) bg-(--color-surface)',
                    )}
                  >
                    <div className="flex gap-3">
                      <input
                        type="radio"
                        checked={checked}
                        onChange={() => setDjiField('missionTemplateId', template.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-[0.923em] font-bold text-(--color-text)">
                          {template.name}
                        </div>
                        <div className="mt-1 text-[0.769em] text-(--color-text3)">
                          {template.description}
                        </div>
                        {checked && template.hasBoxDistance ? (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <FieldLabel className="m-0">Box Distance (m)</FieldLabel>
                            <input
                              type="number"
                              min={100}
                              max={1000}
                              step={10}
                              value={dji.boxDistanceM}
                              onChange={(e) =>
                                setDjiField(
                                  'boxDistanceM',
                                  Number.isFinite(Number(e.target.value))
                                    ? Number(e.target.value)
                                    : 300,
                                )
                              }
                              className="w-[120px] rounded-md border border-(--color-border) bg-(--color-surface) px-2 py-1 text-[0.846em]"
                            />
                            <span className="text-[0.692em] text-(--color-text3)">100–1000 m</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <div>
        <Button onClick={onSave}>Save Configuration</Button>
      </div>
    </div>
  );
}

function AppMonitor({ appId, topicCount }: { appId: AppId; topicCount: number }) {
  const aiEnabled = useAppsStore((s) => s.aiEnabled);
  const dji = useAppsStore((s) => s.dji);
  const activity = useAppsStore((s) => s.activity[appId]);
  const enabledCount = enabledCountForAi(aiEnabled);
  const active = appId === 'aiagent' ? enabledCount > 0 : dji.configured;
  const runtimeCount = activity.filter((item) => item.kind === 'runtime').length;
  const avgResponse = config.isMock && runtimeCount > 0 ? '1.2s' : '—';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'size-2 rounded-full',
            active
              ? 'bg-(--color-green) shadow-[0_0_0_3px_rgba(29,122,64,.16)]'
              : 'bg-(--color-text3)',
          )}
        />
        <span className="text-[0.923em] font-bold text-(--color-text)">
          {active ? 'Running' : 'Stopped'}
        </span>
        {!active ? (
          <span className="text-[0.846em] text-(--color-text3)">
            Finish Configure and Save to start it.
          </span>
        ) : null}
      </div>

      {appId === 'aiagent' ? (
        <div className="grid gap-2 md:grid-cols-3">
          <MetricCard
            value={String(enabledCount)}
            label="Active Contexts"
            icon={<Sparkles size={14} />}
          />
          <MetricCard
            value={String(runtimeCount)}
            label="Assists Logged"
            icon={<Bot size={14} />}
          />
          <MetricCard value={avgResponse} label="Avg Response" icon={<CheckCircle2 size={14} />} />
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-3">
          <MetricCard
            value={dji.configured ? 'Connected' : 'Offline'}
            label="MQTT Broker"
            icon={<Radio size={14} />}
          />
          <MetricCard
            value={String(topicCount)}
            label="Events Received"
            icon={<Route size={14} />}
          />
          <MetricCard
            value={config.isMock ? String(runtimeCount) : '—'}
            label="Missions Triggered"
            icon={<PlaneTakeoff size={14} />}
          />
        </div>
      )}

      <div>
        <SectionTitle>Activity Log</SectionTitle>
        <p className="mb-2 text-[0.769em] text-(--color-text3)">
          When the app ran and what it did.
        </p>
        {!activity.length ? (
          <div className="rounded-md border border-dashed border-(--color-border) bg-(--color-bg) px-3 py-6 text-center text-[0.846em] text-(--color-text3)">
            No activity yet.
          </div>
        ) : (
          <div className="rounded-md border border-(--color-border) bg-(--color-surface)">
            {activity.map((item, idx) => (
              <ActivityRow key={item.id} item={item} last={idx === activity.length - 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({
  value,
  label,
  icon,
}: {
  value: string;
  label: string;
  icon: ReactNode;
}) {
  return (
    <div className="rounded-md border border-(--color-border) bg-(--color-bg) p-3 text-center">
      <div className="mb-1 flex items-center justify-center text-(--color-green)">{icon}</div>
      <div className="text-[1.153em] font-bold text-(--color-text)">{value}</div>
      <div className="mt-1 text-[0.615em] font-bold uppercase tracking-[0.4px] text-(--color-text3)">
        {label}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onToggle,
  strong = false,
  border = false,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  strong?: boolean;
  border?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between py-[9px]',
        border
          ? 'mb-1 border-b-2 border-(--color-border)'
          : 'border-b border-(--color-border-lite)',
      )}
    >
      <span className={cn('text-[0.923em] text-(--color-text)', strong && 'font-bold')}>
        {label}
      </span>
      <Switch checked={checked} onCheckedChange={onToggle} aria-label={label} />
    </div>
  );
}

function ActivityRow({ item, last }: { item: AppActivity; last: boolean }) {
  const when = new Date(item.at);
  return (
    <div className={cn('flex gap-3 px-3 py-2.5', !last && 'border-b border-(--color-border-lite)')}>
      <span className="w-9 shrink-0 font-mono text-[0.692em] text-(--color-text3)">
        {relativeTime(item.at)}
      </span>
      <span className="w-20 shrink-0 font-mono text-[0.692em] text-(--color-text3)">
        {when.toLocaleTimeString()}
      </span>
      <span className="text-[0.846em] text-(--color-text)">{item.text}</span>
    </div>
  );
}

function FieldLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-1 text-[0.769em] font-semibold text-(--color-text2)', className)}>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div className="text-[0.692em] font-bold uppercase tracking-wider text-(--color-text3)">
      {children}
    </div>
  );
}

function enabledCountForAi(aiEnabled: ReturnType<typeof useAppsStore.getState>['aiEnabled']) {
  return AI_AGENT_CONTEXTS.filter((ctx) => aiEnabled[ctx]).length;
}

function relativeTime(iso: string) {
  const diffSec = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (diffSec < 45) return 'now';
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.round(mins / 60)}h`;
}

function djiTriggerSummary() {
  const dji = useAppsStore.getState().dji;
  if (dji.triggerType === 'workflow') return `workflow "${dji.workflowName || 'unnamed'}"`;
  const mission = MISSION_TEMPLATES.find((item) => item.id === dji.missionTemplateId);
  if (!mission) return 'a flight mission';
  return mission.hasBoxDistance
    ? `"${mission.name}" (${dji.boxDistanceM}m box)`
    : `"${mission.name}"`;
}
